import React, {
  forwardRef,
  useState,
  useEffect,
  useRef
} from 'react'
import ReactSVG from 'react-svg'
import PropTypes from 'prop-types'

import arrowDown from '../../../images/select-arrow-down.svg'
import arrowUp from '../../../images/select-arrow-up.svg'
import styles from './select.css'

/**
 * Select component with floating label
 */
const Select = forwardRef(({
  label,
  error,
  value,
  onChange,
  onBlur,
  onFocus,
  disabled,
  name,
  className = '',
  options = [],
  placeholder = '',
  ...rest
}, ref) => {
  const [isFocused, setIsFocused] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const selectRef = useRef(null)

  const hasValue = Boolean(value)
  const hasError = Boolean(error && error.length)

  // Get selected option label
  const selectedOption = options.find(opt => opt.value === value)
  const displayValue = selectedOption ? selectedOption.label : ''

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false)
        setIsFocused(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isOpen])

  const handleToggle = () => {
    if (disabled) return
    setIsOpen(!isOpen)
    setIsFocused(!isOpen)
    if (!isOpen) {
      onFocus?.()
    } else {
      onBlur?.()
    }
  }

  const handleOptionClick = (optionValue) => {
    onChange?.(optionValue)
    setIsOpen(false)
    setIsFocused(false)
    onBlur?.()
  }

  const handleKeyDown = (e) => {
    if (disabled) return

    switch (e.key) {
      case 'Enter':
        if (!isOpen) {
          e.preventDefault()
          setIsOpen(prev => !prev)
          setIsFocused(true)
        } else if (isOpen && hoveredIndex !== null) {
          e.preventDefault()
          handleOptionClick(options[hoveredIndex].value)
        } else {
          setIsOpen(false)
          setIsFocused(false)
        }
        break

      case 'ArrowDown':
        e.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
          setHoveredIndex(0)
        } else {
          setHoveredIndex(prev => {
            if (prev === null || prev === options.length - 1) {
              return 0
            }
            return prev + 1
          })
        }
        break

      case 'ArrowUp':
        e.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
          setHoveredIndex(options.length - 1)
        } else {
          setHoveredIndex(prev => {
            if (prev === null || prev === 0) {
              return options.length - 1
            }
            return prev - 1
          })
        }
        break

      case 'Escape':
        setIsOpen(false)
        setIsFocused(false)
        break

      default:
        setIsOpen(false)
        setIsFocused(false)
        break
    }
  }

  const containerClass = [
    styles.inputContainer,
    hasError && styles.error,
    isFocused && styles.focused,
    disabled && styles.disabled
  ].filter(Boolean).join(' ')

  const labelClass = [
    styles.label,
    (hasValue || isFocused) && styles.labelFloating
  ].filter(Boolean).join(' ')

  return (
    <div className={`${styles.fieldWrapper} ${className}`}>
      <div
        ref={selectRef}
        className={containerClass}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="gender-options"
        {...rest}
      >
        <label htmlFor={name} className={labelClass}>
          {label}
        </label>

        <div className={styles.selectedValue}>
          {displayValue}
        </div>

        <div className={styles.arrow}>
          <ReactSVG
            src={isOpen ? arrowUp : arrowDown}
            beforeInjection={svg => {
              svg.setAttribute('class', styles.arrowIcon)
            }}
          />
        </div>

        {isOpen && (
          <div className={styles.dropdown}>
            {options.map((option, index) => {
              const optionClass = [
                styles.option,
                hoveredIndex === index && styles.optionHover,
                index === options.length - 1 && styles.optionLast
              ].filter(Boolean).join(' ')

              return (
                <div
                  key={option.value}
                  className={optionClass}
                  tabIndex={-1}
                  aria-selected={hoveredIndex === index}
                  role="option"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOptionClick(option.value)
                  }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {option.label}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {hasError && <span className={styles.errorMessage}>{error}</span>}
    </div>
  )
})

Select.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string
}

export default Select
